import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Map, Marker, GeoJson, Overlay } from 'pigeon-maps';
import {
  getVehicleLocationLogs,
  resetVehicleLocationLogs,
} from 'src/redux/Actions/Vehicle';
import { ReactComponent as EmptyMap } from 'src/Images/icons/EmptyStates/emptyMap.svg';

const locationTabBottomPadding = 20;
const defaultMapCenter = [37.7749, -122.4194];

function getRouteZoom(locations) {
  if (locations.length <= 1) return 14;

  const latitudes = locations.map((location) => Number(location.latitude));
  const longitudes = locations.map((location) => Number(location.longitude));
  const latitudeSpan = Math.max(...latitudes) - Math.min(...latitudes);
  const longitudeSpan = Math.max(...longitudes) - Math.min(...longitudes);
  const maxSpan = Math.max(latitudeSpan, longitudeSpan);

  if (maxSpan > 20) return 3;
  if (maxSpan > 10) return 4;
  if (maxSpan > 5) return 5;
  if (maxSpan > 2) return 6;
  if (maxSpan > 1) return 8;
  if (maxSpan > 0.5) return 9;
  if (maxSpan > 0.2) return 10;
  if (maxSpan > 0.1) return 11;
  if (maxSpan > 0.05) return 12;
  return 13;
}

function RouteEndpoint({ color, label }) {
  return (
    <div className="vehicle-location-endpoint" title={label}>
      <svg width={22} height={22} viewBox="0 0 22 22" aria-hidden="true">
        <circle cx="11" cy="11" r="9" fill="#ffffff" />
        <circle cx="11" cy="11" r="6" fill={color} />
      </svg>
    </div>
  );
}

export default function LocationTab({ vehicle }) {
  const dispatch = useDispatch();
  const mapContainerRef = useRef(null);
  const [mapHeight, setMapHeight] = useState(300);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [filterFormData, setFilterFormData] = useState(() => {
    const date = new Date();
    const today = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .split('T')[0];

    return {
      startDate: today,
      endDate: today,
      startTime: '',
      endTime: '',
    };
  });
  const [appliedFilters, setAppliedFilters] = useState(filterFormData);
  const tenantOrganisationId = useSelector(
    (state) => state.AuthReducer?.userData?.organisations?.id,
  );
  const themeColor = useSelector(
    (state) => state.AuthReducer?.userData?.tenant?.theme,
  );
  const vehicleLocationLogs = useSelector(
    (state) => state.VehicleReducer.vehicleLocationLogs,
  );
  const plottedLocations = vehicleLocationLogs.locations || [];
  const mapCenter = useMemo(() => {
    if (!plottedLocations.length) return defaultMapCenter;

    const latitudes = plottedLocations.map((location) =>
      Number(location.latitude),
    );
    const longitudes = plottedLocations.map((location) =>
      Number(location.longitude),
    );

    return [
      (Math.min(...latitudes) + Math.max(...latitudes)) / 2,
      (Math.min(...longitudes) + Math.max(...longitudes)) / 2,
    ];
  }, [plottedLocations]);
  const mapZoom = useMemo(
    () => getRouteZoom(plottedLocations),
    [plottedLocations],
  );
  const firstLocation = plottedLocations[0];
  const latestLocation = plottedLocations[plottedLocations.length - 1];
  const mapKey = latestLocation
    ? `${firstLocation?.id || 'start'}-${latestLocation.id || 'end'}-${plottedLocations.length}`
    : 'empty';

  const updateMapHeight = useCallback(() => {
    if (!mapContainerRef.current) return;

    const { top } = mapContainerRef.current.getBoundingClientRect();
    const availableHeight = window.innerHeight - top - locationTabBottomPadding;

    setMapHeight(Math.max(1, Math.floor(availableHeight)));
  }, []);

  useLayoutEffect(() => {
    updateMapHeight();
    window.addEventListener('resize', updateMapHeight);

    return () => {
      window.removeEventListener('resize', updateMapHeight);
    };
  }, [updateMapHeight, plottedLocations.length, isLoading, loadError]);

  useEffect(() => {
    let isMounted = true;

    if (!tenantOrganisationId || !vehicle?.id) {
      dispatch(resetVehicleLocationLogs());
      return undefined;
    }

    setIsLoading(true);
    setLoadError('');

    const locationLogFilters = {};

    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        locationLogFilters[key] = value;
      }
    });

    dispatch(
      getVehicleLocationLogs(
        tenantOrganisationId,
        vehicle.id,
        locationLogFilters,
      ),
    )
      .catch(() => {
        if (isMounted) {
          setLoadError('Unable to load vehicle location logs.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
      dispatch(resetVehicleLocationLogs());
    };
  }, [appliedFilters, dispatch, tenantOrganisationId, vehicle?.id]);

  const handleFilterInputChange = (event) => {
    const { name, value } = event.target;

    setFilterFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();
    setAppliedFilters(filterFormData);
  };

  const handleResetFilters = () => {
    const date = new Date();
    const today = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .split('T')[0];
    const defaultFilters = {
      startDate: today,
      endDate: today,
      startTime: '',
      endTime: '',
    };

    setFilterFormData(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const renderMapContent = () => {
    if (isLoading) {
      return (
        <div className="vehicle-master-location-map">
          <span className="text-sm-outfit text-medium-bold">
            Loading vehicle locations...
          </span>
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="vehicle-master-location-map">
          <EmptyMap className="vehicle-master-empty-map" />
          <span className="text-sm-outfit text-medium-bold">{loadError}</span>
        </div>
      );
    }

    return (
      <div ref={mapContainerRef} className="vehicle-location-map-shell">
        <Map
          key={mapKey}
          height={mapHeight}
          defaultCenter={mapCenter}
          defaultZoom={mapZoom}
        >
          {plottedLocations.length > 1 && (
            <GeoJson
              data={{
                type: 'FeatureCollection',
                features: [
                  {
                    type: 'Feature',
                    geometry: {
                      type: 'LineString',
                      coordinates: plottedLocations.map((location) => [
                        Number(location.longitude),
                        Number(location.latitude),
                      ]),
                    },
                  },
                ],
              }}
              styleCallback={() => ({
                stroke: '#11334e',
                strokeWidth: 4,
              })}
            />
          )}

          {plottedLocations.length > 1 && (
            <Overlay
              anchor={[
                Number(latestLocation.latitude),
                Number(latestLocation.longitude),
              ]}
              offset={[11, 11]}
            >
              <RouteEndpoint color="#e7332f" label="Latest location" />
            </Overlay>
          )}

          {plottedLocations.length > 1 && (
            <Overlay
              anchor={[
                Number(firstLocation.latitude),
                Number(firstLocation.longitude),
              ]}
              offset={[11, 11]}
            >
              <RouteEndpoint color="#667085" label="Start location" />
            </Overlay>
          )}

          {plottedLocations.length === 1 && (
            <Marker
              anchor={[
                Number(firstLocation.latitude),
                Number(firstLocation.longitude),
              ]}
              width={40}
            />
          )}
        </Map>
      </div>
    );
  };

  return (
    <div className="horizontal-padding-10 vertical-padding-20 div-flex-column rg-10">
      <div className="vehicle-location-header div-space-between cg-10">
        <span className="text-md-outfit text-medium-bold text-nowrap">
          Locations for {vehicle?.vehicleNumber || '-/-'}
        </span>
        <form
          className="vehicle-location-filter-form"
          onSubmit={handleApplyFilters}
        >
          <label
            className="vehicle-location-filter-field"
            htmlFor="location-start-date"
          >
            <span className="text-xs-outfit secondary-text-grey">
              Start Date
            </span>
            <input
              id="location-start-date"
              type="date"
              name="startDate"
              className="form-input-section"
              value={filterFormData.startDate}
              onChange={handleFilterInputChange}
            />
          </label>
          <label
            className="vehicle-location-filter-field"
            htmlFor="location-start-time"
          >
            <span className="text-xs-outfit secondary-text-grey">
              Start Time
            </span>
            <input
              id="location-start-time"
              type="time"
              name="startTime"
              className="form-input-section"
              value={filterFormData.startTime}
              onChange={handleFilterInputChange}
            />
          </label>
          <label
            className="vehicle-location-filter-field"
            htmlFor="location-end-date"
          >
            <span className="text-xs-outfit secondary-text-grey">End Date</span>
            <input
              id="location-end-date"
              type="date"
              name="endDate"
              className="form-input-section"
              value={filterFormData.endDate}
              onChange={handleFilterInputChange}
            />
          </label>
          <label
            className="vehicle-location-filter-field"
            htmlFor="location-end-time"
          >
            <span className="text-xs-outfit secondary-text-grey">End Time</span>
            <input
              id="location-end-time"
              type="time"
              name="endTime"
              className="form-input-section"
              value={filterFormData.endTime}
              onChange={handleFilterInputChange}
            />
          </label>
          <div className="vehicle-location-filter-actions">
            <button
              type="button"
              className={`common-btn-outlined border-${themeColor}`}
              onClick={handleResetFilters}
            >
              Reset
            </button>
            <button
              type="submit"
              className={`common-btn theme-background-${themeColor}`}
            >
              Apply
            </button>
          </div>
        </form>
      </div>
      {renderMapContent()}
    </div>
  );
}
